import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useAuthContext, useHasPermission } from '../../../../contexts/auth';
import { formatDate, formatNumber, formatQuantity } from '../../../../utils';
import CustomModal from '../../../../components/ui/modal/modal.component';
import { PaymentCardForm } from '../../../../components/paymentCardForm/paymentCardForm.component';
import { BankTransferDetailsPanel } from '../../../../components/bankTransferDetails/bankTransferDetails.component';
import { useSetPaymentMethod } from '../../../../api/subscriptions';
import { PaymentMethod } from '../../../../types/subscriptions';
import { ERROR_SNACKBAR, INFO_SNACKBAR, SUCCESS_SNACKBAR } from '../../../../consts';
import {
  DetailRow,
  DetailSection,
  DetailValue,
  InfoHint,
} from '../../../../components/ui/detailRow/detailRow.component';
import { basicInfoContainerStyle } from '../../../../styles/commonStyles';
import { SubscriptionCancellation } from './subscriptionCancellation.component';
import styles from './tenantSubscriptionInfo.module.css';

export const TenantSubscriptionInfo = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser, currentUserLoading } = useAuthContext();
  const tenant = currentUser?.tenant;
  const canViewSubscription = useHasPermission('payment.view_tenantsubscription');
  const canManageSubscription = useHasPermission('payment.change_tenantsubscription');
  const [cardModalOpen, setCardModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { loading: switchingMethod, setPaymentMethod } = useSetPaymentMethod();

  const switchPaymentMethod = async (method: PaymentMethod) => {
    const { success } = await setPaymentMethod(method);
    if (!success) {
      enqueueSnackbar(t('Could not change the payment method. Please try again.'), ERROR_SNACKBAR);
      return;
    }
    enqueueSnackbar(
      method === 'BANK_TRANSFER' ? t('You now pay by bank transfer.') : t('You now pay by card.'),
      SUCCESS_SNACKBAR
    );
    window.dispatchEvent(new Event('tenantUpdated'));
  };

  // Returning from a PayU 3DS card-verification redirect. The card is stored
  // by the notification webhook, so refresh the tenant and let the user know.
  useEffect(() => {
    if (searchParams.get('card_setup') === '1') {
      enqueueSnackbar(
        t('Verifying your card… this can take a moment.'),
        INFO_SNACKBAR
      );
      const timer = setTimeout(
        () => window.dispatchEvent(new Event('tenantUpdated')),
        2500
      );
      searchParams.delete('card_setup');
      setSearchParams(searchParams, { replace: true });
      return () => clearTimeout(timer);
    }
  }, [searchParams, setSearchParams, enqueueSnackbar, t]);

  if (!canViewSubscription) {
    return null;
  }

  if (currentUserLoading || !tenant) {
    return (
      <Box className={styles.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (!tenant.subscription) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('No subscription information available')}
      </Typography>
    );
  }

  const { subscription } = tenant;
  const statusMap: Record<string, string> = {
    T: t('Trial'),
    A: t('Active'),
    D: t('Past due'),
    C: t('Canceled'),
    E: t('Expired'),
  };
  const statusLabel = statusMap[subscription.status] ?? subscription.status;

  // Enterprise plan whose individual price sales has not entered yet: showing
  // the stored 0.00 would be misleading.
  const customPricePending =
    subscription.is_custom_priced &&
    !subscription.custom_price_net &&
    (!subscription.price_net || parseFloat(subscription.price_net) === 0);

  const paysByTransfer = subscription.payment_method === 'BANK_TRANSFER';

  const chargeHint = paysByTransfer
    ? subscription.status === 'T'
      ? t(
          'No charge during the trial. When it ends, transfer a prorated amount for the rest of that month, then the full plan fee each month, in advance.'
        )
      : t(
          'The plan fee is due in advance on the 1st of each calendar month; transfer it within {{days}} days using the title shown under Payment method. An upgrade is due immediately (prorated); a downgrade takes effect from the 1st of next month.',
          { days: subscription.bank_transfer?.payment_term_days ?? '' }
        )
    : subscription.status === 'T'
      ? t(
          'No charge during the trial. When it ends, add a payment card — a prorated amount for the rest of that month is charged to it, then the full plan fee on the 1st of every month, in advance.'
        )
      : t(
          'The plan fee is charged automatically to your saved card in advance on the 1st of each calendar month. An upgrade is charged immediately (prorated); a downgrade takes effect and is charged from the 1st of next month.'
        );

  const text = (value: React.ReactNode) => <DetailValue>{value}</DetailValue>;

  return (
    <Box sx={basicInfoContainerStyle}>
      <DetailSection title={t('Plan')}>
        <DetailRow label={t('Subscription tariff')} value={text(subscription.tariff?.name || '')} />
        <DetailRow
          label={t('Status')}
          value={text(statusLabel)}
          noBorder={
            !subscription.pending_tariff && subscription.status !== 'D'
          }
        />
        {subscription.pending_tariff && (
          <DetailRow
            label={t('Scheduled change')}
            value={
              <Typography variant="subtitle1" color="warning.main">
                {t('Downgrade to {{plan}} takes effect on {{date}}.', {
                  plan: subscription.pending_tariff.name,
                  date: subscription.pending_tariff_effective
                    ? formatDate(subscription.pending_tariff_effective)
                    : '',
                })}
              </Typography>
            }
            noBorder={subscription.status !== 'D'}
          />
        )}
        {subscription.status === 'D' && (
          <DetailRow
            label={t('Payment')}
            value={
              <Typography variant="subtitle1" color="error.main">
                {paysByTransfer
                  ? t('We have not received your transfer yet. Please pay the amount shown under Payment method.')
                  : t('We could not process your payment. Next attempt on {{date}}.', {
                      date: subscription.next_retry_at
                        ? formatDate(subscription.next_retry_at)
                        : '',
                    })}
              </Typography>
            }
            noBorder
          />
        )}
      </DetailSection>

      <DetailSection title={t('Billing')}>
        {subscription.current_period_start && subscription.current_period_end && (
          <DetailRow
            label={t('Billing period')}
            value={text(
              `${formatDate(subscription.current_period_start)} – ${formatDate(
                subscription.current_period_end
              )}`
            )}
          />
        )}
        <DetailRow
          label={
            <>
              {t('Paid until')}
              <InfoHint text={chargeHint} />
            </>
          }
          value={text(formatDate(subscription.paid_until))}
          noBorder={subscription.equipment_limit === null}
        />
        {subscription.equipment_limit !== null && (
          <DetailRow
            label={
              <>
                {t('Device usage')}
                <InfoHint
                  text={t(
                    'Measured at each month close — the number of active devices with settlements in the previous calendar month. Shown as "—" until the first month-end.'
                  )}
                />
              </>
            }
            value={text(
              `${subscription.measured_device_count ?? '—'} / ${subscription.equipment_limit}`
            )}
            noBorder
          />
        )}
      </DetailSection>

      <DetailSection title={t('Price')}>
        <DetailRow
          label={
            subscription.is_custom_priced ? (
              <>
                {t('Net price')}
                <InfoHint text={t('We will contact you to discuss pricing.')} />
              </>
            ) : (
              t('Net price')
            )
          }
          value={text(
            customPricePending ? t('Individual pricing') : formatNumber(subscription.price_net)
          )}
        />
        <DetailRow
          label={t('VAT')}
          value={text(
            customPricePending
              ? '—'
              : subscription.vat
                ? `${formatQuantity(subscription.vat)}%`
                : ''
          )}
        />
        <DetailRow
          label={t('Total price')}
          value={text(customPricePending ? '—' : formatNumber(subscription.total))}
          noBorder
        />
      </DetailSection>

      {!subscription.is_custom_priced && (
        <DetailSection title={t('Payment method')}>
          <DetailRow
            label={t('Payment method')}
            value={text(paysByTransfer ? t('Bank transfer') : t('Card'))}
            noBorder={paysByTransfer}
          />
          {paysByTransfer ? (
            <Box sx={{ p: 1.5 }}>
              <BankTransferDetailsPanel
                details={subscription.bank_transfer}
                transfers={subscription.pending_transfers}
              />
            </Box>
          ) : (
            <>
              <DetailRow
                label={
                  <>
                    {t('Card')}
                    <InfoHint
                      text={t(
                        'Subscription fees are charged automatically to this card. You can replace it at any time.'
                      )}
                    />
                  </>
                }
                value={
                  subscription.card
                    ? text(`${subscription.card.brand ?? ''} ${subscription.card.masked ?? ''}`.trim())
                    : text(
                        <Typography
                          variant="subtitle1"
                          color={subscription.needs_card ? 'error.main' : 'text.secondary'}
                        >
                          {t('No payment card on file')}
                        </Typography>
                      )
                }
                noBorder={!subscription.card?.expiration}
              />
              {subscription.card?.expiration && (
                <DetailRow
                  label={t('Card expires')}
                  value={text(subscription.card.expiration)}
                  noBorder
                />
              )}
              {!subscription.payu.enabled && (
                <DetailRow
                  label={t('Payment')}
                  value={
                    <Typography variant="subtitle1" color="warning.main">
                      {t('Card payments are currently unavailable.')}
                    </Typography>
                  }
                  noBorder
                />
              )}
            </>
          )}
          {canManageSubscription && (
            <Box sx={{ p: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {paysByTransfer ? (
                subscription.payu.enabled && (
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={switchingMethod}
                    // With a card already on file just switch back to it; otherwise
                    // saving a new card switches on its own.
                    onClick={() =>
                      subscription.card ? switchPaymentMethod('CARD') : setCardModalOpen(true)
                    }
                  >
                    {t('Pay by card')}
                  </Button>
                )
              ) : (
                <>
                  {subscription.payu.enabled && (
                    <Button
                      variant={subscription.needs_card ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => setCardModalOpen(true)}
                    >
                      {subscription.card ? t('Update card') : t('Add card')}
                    </Button>
                  )}
                  {subscription.bank_transfer?.is_enabled && (
                    <Button
                      variant="text"
                      size="small"
                      disabled={switchingMethod}
                      onClick={() => switchPaymentMethod('BANK_TRANSFER')}
                    >
                      {t('Pay by bank transfer')}
                    </Button>
                  )}
                </>
              )}
            </Box>
          )}
        </DetailSection>
      )}

      {(subscription.cancellation_pending || subscription.can_cancel) && (
        <SubscriptionCancellation subscription={subscription} />
      )}

      <CustomModal
        opened={cardModalOpen}
        setOpened={setCardModalOpen}
        title={subscription.card ? t('Update card') : t('Add card')}
        minWidth={480}
        content={
          <PaymentCardForm
            payu={subscription.payu}
            submitLabel={subscription.card ? t('Update card') : t('Add card')}
            onSuccess={() => {
              setCardModalOpen(false);
              window.dispatchEvent(new Event('tenantUpdated'));
            }}
          />
        }
      />
    </Box>
  );
};
